import { initializeApp } from "https://www.gstatic.com/firebasejs/12.12.1/firebase-app.js";
// Two absences here are deliberate.
//
// createUserWithEmailAndPassword — accounts are created by the accountApi Cloud
//   Function now, because the access code has to be checked somewhere the
//   browser cannot argue with.
// sendEmailVerification — every confirmation mail goes through our own outbox,
//   from the domain verified in Resend. Firebase's built-in mailer sends from
//   noreply@<project>.firebaseapp.com, leaves no record we can read, and
//   reports its failures to a console nobody is watching. A confirmation that
//   goes missing has to be answerable afterwards.
//
// sendPasswordResetEmail is knowingly still Firebase's own. Moving it would
// mean minting reset links server-side too, which is a bigger change than this
// one and has not gone wrong.
import { getAuth, connectAuthEmulator, onAuthStateChanged, signInWithEmailAndPassword, signOut, updateProfile, sendPasswordResetEmail, deleteUser, reauthenticateWithCredential, EmailAuthProvider } from "https://www.gstatic.com/firebasejs/12.12.1/firebase-auth.js";
import { initializeFirestore, connectFirestoreEmulator, persistentLocalCache, persistentMultipleTabManager, doc, setDoc, getDoc, addDoc, collection, query, where, getDocs, orderBy, limit, deleteDoc, deleteField, onSnapshot, updateDoc, arrayUnion, arrayRemove, runTransaction } from "https://www.gstatic.com/firebasejs/12.12.1/firebase-firestore.js";
import { getStorage, connectStorageEmulator, ref as storageRef, uploadBytes, getDownloadURL, listAll, deleteObject } from "https://www.gstatic.com/firebasejs/12.12.1/firebase-storage.js";
import { initAppCheck } from "./app-check.js";
import { USE_EMULATORS, EMULATORS } from "./backend.js";

// Firebase configuration
const firebaseConfig = {
    apiKey: "AIzaSyAp39wHH3zVdoVdSA71EI3Dm75DgbU4Wzs",
    authDomain: "tournamentmanager-6df09.firebaseapp.com",
    projectId: "tournamentmanager-6df09",
    storageBucket: "tournamentmanager-6df09.firebasestorage.app",
    messagingSenderId: "82673723093",
    appId: "1:82673723093:web:86502d68b974c8a730a45c",
    measurementId: "G-YRW6S0NRKX"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
// Before any other service: App Check has to be attaching tokens by the time the
// first Firestore request goes out. A no-op until a reCAPTCHA site key is set —
// see js/services/app-check.js for what it protects and how to switch it on.
initAppCheck(app);
const auth = getAuth(app);
// ignoreUndefinedProperties: a single `undefined` field anywhere in a payload makes
// setDoc throw invalid-argument and reject the whole write. This drops undefined
// fields instead, so one stray value can never block a save again.
//
// localCache: the SDK defaults to memory-only, which meant a reload with no
// connection came back with nothing from Firestore at all — the tournament itself
// survived in our own IndexedDB, but everything read live (registrations, the
// watched tournament, the user document) was simply gone until the wifi returned.
// A persistent cache serves the last snapshot instead, and queues writes made
// offline until there is somewhere to send them. This is the data half of the
// offline story; the service worker (scripts/sw-template.js) is the code half,
// and neither is much use in a playing hall without the other.
//
// The multi-tab manager is not optional here: arbiters routinely have the
// pairings open in one tab and standings in another, and the single-tab default
// lets only the FIRST tab hold the lease — every other tab then fails to
// initialise persistence at all.
const db = initializeFirestore(app, {
    ignoreUndefinedProperties: true,
    localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() })
});
const storage = getStorage(app);

// ===== The local emulators ===================================================
//
// Whether to use them is decided once, in ./backend.js: by default on
// localhost, never on a deployed host. The connect* calls must happen before any
// read or write goes out, which is why they sit here, immediately after the
// three services are created, rather than in whichever module happens to need
// Firestore first.
if (USE_EMULATORS) {
    // disableWarnings silences the SDK's banner, which is a console.info on every
    // load; the ports are the ones in firebase.json's `emulators` block.
    connectAuthEmulator(auth, EMULATORS.auth, { disableWarnings: true });
    connectFirestoreEmulator(db, EMULATORS.firestore.host, EMULATORS.firestore.port);
    connectStorageEmulator(storage, EMULATORS.storage.host, EMULATORS.storage.port);
    console.info('Zugzwang is talking to the local Firebase emulators. For the real project, set localStorage.zwProd = "1" in your own browser (js/services/backend.js).');
}

// Everything above used to sit at the top of firebase-auth.js. It lives here so
// that the other service modules can share ONE configured app, and so that the
// gstatic version string is written down in exactly one place: the SDK functions
// the rest of js/services/ needs are re-exported below rather than re-imported
// from the CDN in every file, where the versions could drift apart unnoticed.
export { app, auth, db, storage };

export {
    onAuthStateChanged, signInWithEmailAndPassword, signOut, updateProfile,
    sendPasswordResetEmail, deleteUser, reauthenticateWithCredential, EmailAuthProvider
};

export {
    doc, setDoc, getDoc, addDoc, collection, query, where, getDocs, orderBy, limit,
    deleteDoc, deleteField, onSnapshot, updateDoc, arrayUnion, arrayRemove, runTransaction
};

export { storageRef, uploadBytes, getDownloadURL, listAll, deleteObject };
